import React, { useEffect, useMemo, useState } from 'react';
import Downloading from '@/pages/opinion/assets/imgs/downloading.png';
import XlsxIcon from '@/pages/opinion/assets/svg/xlsx-icon.svg';
import TxtIcon from '@/pages/opinion/assets/svg/txt-icon.svg';
import { Icon } from 'tea-component';
import { connect, useIntl } from 'umi';
import { urlToMap } from '@op/utils/url';
import {
  getDownloadData,
  getDownloadConfig,
  parseDownloadParams,
  getFileName,
} from './utils';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import CacheExpiredTips from '@op/components/CacheExpiredTips';
import './index.scss';
import { createExcel, createPageExcel } from './utils/excelFile';
import { transNumber } from '@op/components/DownloadCommon/download-page/utils';
import { ColumnsType, DownloadFuncParamsType, DownloadTipsType, SheetType } from './types';
import { DBO_T } from '@op/utils/format';
import BubbleCommon from '@op/components/BubbleCommon';
import { CONTACT_EMAIL } from '@op/constants';

const errorTextMap: { [key in DownloadTipsType]: string } = {
  GPT_nodata:
    'Due to insufficient selected content, GPT is currently unable to summarize the report as requested.',
  GPT_failed:
    'Due to the instability of the GPT API, GPT is currently unable to summarize the report as requested.',
  GPT_cancel: '',
  file_failed: 'Download failed, please refresh the page.',
  data_max: 'Sorry, the export limit is set to {value} rows.',
  common: 'Download failed, please refresh the page.',
  empty: '',
  GPT_maxLimit: '',
  GPT_nodataSuccess: '',
  GPT_urlError: '',
  cos_failed:
    'Download failed due to excessive data. We recommend exporting in batches or applying additional filters.',
  network_error:
    'Network connection seems unstable. Please check your network settings or try again later.',
  server_error:
    'The server is currently busy or the resource is temporarily unavailable. Please try again later.',
  other_error:
    'Encountered an issue with the download. Please try again later or contact {contact} for assistance.',
};

// 服务器/下载源繁忙错误码（后端返回）
const SERVER_BUSY_CODE = 30023;

/**
 * 根据异常对象判断下载错误的类型
 * - 网络问题（前端判断）：请求层抛出的网络异常（如 Network Error、超时等）
 * - 服务器/下载源问题：后端返回 30023 错误码
 * - 其他错误：兜底
 */
function getDownloadErrorType(error: any): DownloadTipsType {
  const code = error?.code ?? error?.data?.code;

  // 后端返回 30023，代表服务器繁忙或资源暂时不可用
  if (code === SERVER_BUSY_CODE) {
    return 'server_error';
  }

  // 前端判断网络问题：axios 网络层错误（无 code 或 code 为兜底 999，且 message 命中网络异常关键词）
  const message = (error?.msg || error?.message || '').toString().toLowerCase();
  if (
    code === undefined ||
    code === 999 ||
    /network error|timeout|network|failed to fetch|load failed/i.test(message)
  ) {
    return 'network_error';
  }

  return 'other_error';
}



export function getExcelColumns(columns?: ColumnsType, params?: any) {
  return (sheet: SheetType) => {
    return (Array.isArray(columns) ? columns : columns?.(params, sheet)) || [];
  }
}


export function DownloadPage({
  gameInfo: pageGameInfo,
  wholeGameInfo: pageWholeGameInfo,
}: {
  gameInfo: any;
  wholeGameInfo: any;
}) {
  // 参考 https://databrain.intlgame.com/intelligence_detail/download.html?entityType=mobile&taskId=2825498722

  const { formatMessage } = useIntl();
  const map = urlToMap(window.location.href);
  const { readParams } = useDownloadParams();
  /** 参数读取失败（缓存失效）时展示兜底提示 */
  const [invalid, setInvalid] = useState(false);

  const [fileList, setFileList] = useState<
    Array<{ text: string; type: 'xlsx' | 'txt' }>
  >([{ text: 'filename', type: 'xlsx' }]);
  const [downloadTips, setDownloadTips] = useState<DownloadTipsType>('empty');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading',
  );
  console.log("map", map);
  useEffect(() => {
    async function loadParams() {
      const loadingType = map.get('type');

      let downloadData: any = {};

      if (loadingType === 'advancedGptData') {
        downloadData = {
          key: 'GptAdvancedDataDownload',
          fileName: 'Advanced AI Report Reviews',
          filter: {
            task_id: map.get('task_id'),
            ...pageGameInfo,
          },
          gameInfo: pageWholeGameInfo,
        };
      } else {
        // 方案 1：从服务端/本地存储读取（由 Hook 内部处理）
        downloadData = await readParams();
      }

      // 方案 2：原有 localStorage 逻辑（兼容无 _s 标记的旧 URL）
      if (!downloadData || Object.keys(downloadData).length === 0) {
        downloadData = parseDownloadParams(map);
        
      }

      // 无数据情况处理：缓存失效，展示兜底提示
      if (downloadData === null) {
        setInvalid(true);
        return;
      }
      const { fileName, filter, gameInfo } = downloadData;
      const config = getDownloadConfig({
        ...downloadData,
        wholeGameInfo: pageWholeGameInfo,
      });
      // eslint-disable-next-line prefer-const
      let { files, type } = config;

      if (files && files.length > 0) {
        files = files.map((item) => {
          return {
            ...item,
            fileName: getFileName(item.fileName, filter, gameInfo, ''),
          };
        });
        setFileList(
          files.map((item) => ({
            text: item.fileName,
            type: item.type === 'txt' ? 'txt' : 'xlsx',
          })),
        );
      } else {
        setFileList([{ text: fileName, type: 'xlsx' }]);
      }

      const funcParams = {
        params: downloadData,
        files,
        fileName,
        config,
      };

      if (type === 'zip' && files && files.length > 0) {
        downloadZip(funcParams);
      } else if (type === 'page') {
        downloadPage(funcParams);
      } else if (type === 'fileZip') {
        downloadFileZip(funcParams);
      } else {
        download(funcParams);
      }
    }
    loadParams();
  }, []);

  async function download({
    params,
    fileName,
    config,
  }: DownloadFuncParamsType) {
    // 获取参数阶段
    try {
      // 请求数据,并处理为最终数据
      const data = await getDownloadData({
        ...params,
        callbacks: {
          begin: () => { },
          end: () => {
            setStatus('success');
          },
          error: ({ tips = 'empty' }) => {
            setDownloadTips(tips);
            setStatus('error');
          },
        },
      });

      // 直接文件下载就结束
      if (data === null) return;

      const { columns, sheetNames = 'Sheet' } = config;

      await createExcel({ sheetNames, list: data, fileName, columns: getExcelColumns(columns, params) });
    } catch (e) {
      setStatus('error');
      setDownloadTips(getDownloadErrorType(e));
      console.log('error download', e);
    }
  }

  async function downloadZip({
    params,
    fileName,
    files = [],
  }: DownloadFuncParamsType) {
    // 获取参数阶段
    try {
      // 请求数据,并处理为最终数据
      const data: any = await getDownloadData({
        ...params,
        callbacks: {
          begin: () => { },
          end: () => setStatus('success'),
          error: ({ tips = 'empty' }) => {
            setDownloadTips(tips);
            setStatus('error');
          },
        },
      });

      const list = [];
      for (const item of files) {
        const { key, sheetNames = 'Sheet', type, fileName, columns } = item;
        const realFileName = `${fileName}.${type}`;
        const renderData = data?.[key];
        if (type === 'xlsx') {

          const blob = await createExcel(
            {
              sheetNames,
              list: renderData,
              fileName,
              columns: getExcelColumns(columns, params),
            },
            false,
          );
          list.push({ fileName: realFileName, data: blob });
        } else {
          list.push({ fileName: realFileName, data: renderData });
        }
      }
      DBO_T.zip(list, fileName);
    } catch (e) {
      console.log(e);
      setStatus('error');
      setDownloadTips(getDownloadErrorType(e));
    }
  }

  async function downloadFileZip({
    params,
    fileName,
  }: DownloadFuncParamsType) {
    try {
      const data: any = await getDownloadData({
        ...params,
        callbacks: {
          begin: () => {
            console.log('begin');
          },
          end: () => setStatus('success'),
          error: ({ tips = 'empty' }) => {
            setDownloadTips(tips);
            setStatus('error');
          },
        },
      });

      console.log(data);

      if (data?.fileData) {
        DBO_T.zip(data.fileData, fileName);
        setStatus('success');
      } else {
        setDownloadTips('file_failed');
        setStatus('error');
      }
    } catch (e) {
      console.log(e);
      setStatus('error');
      setDownloadTips('file_failed');
    }
  }

  async function downloadPage({
    params,
    fileName,
    config,
  }: DownloadFuncParamsType) {
    try {
      const pageSize = 1000;
      let page = 0;
      let totalPage = 0;
      let total = 0;
      const { filter } = params;
      const {
        sheetNames = 'Sheet',
        columns,
        valueKey = 'list',
        totalKey = 'total',
      } = config;

      const excel = await createPageExcel({ sheetNames, fileName, columns: getExcelColumns(columns, params) });

      do {
        page = page + 1;
        const data: any = await getDownloadData({
          ...params,
          filter: { ...filter, page, page_size: pageSize },
          callbacks: {
            begin: () => {
              setStatus('loading');
            },
            end: () => {
              setStatus('success');
            },
            // eslint-disable-next-line no-loop-func
            error: () => {
              setStatus('error');
              page = Infinity;
            },
          },
        });
        const list = data?.[valueKey];
        excel.add(typeof sheetNames === 'string' ? { list } : list);
        if (totalPage === 0) {
          total = data?.[totalKey];
          totalPage = Math.ceil((data?.total || 0) / pageSize);
        }
      } while (page < totalPage);
      excel.finally(total);
      excel.create();
    } catch (error) {
      console.log(error);
      setStatus('error');
      setDownloadTips(getDownloadErrorType(error));
    }
  }

  const { errorText, showError } = useMemo(() => {
    const text = errorTextMap[downloadTips];

    if (text) {
      if (downloadTips === 'data_max') {
        return {
          showError: true,
          errorText: formatMessage(
            { id: text },
            { value: transNumber(1000000) },
          ),
        };
      }

      if (downloadTips === 'other_error') {
        return {
          showError: true,
          errorText: formatMessage(
            { id: text },
            { contact: CONTACT_EMAIL },
          ),
        };
      }

      return { showError: true, errorText: formatMessage({ id: text }) };
    }

    return { showError: false, errorText: '' };
  }, [downloadTips]);

  // 缓存失效：展示统一兜底提示
  if (invalid) {
    return (
      <div className="dbo-down-page-wrap">
        <CacheExpiredTips scene="download" height={"100vh"} />
      </div>
    );
  }

  return (
    <div className="dbo-down-page-wrap">
      <div className="dbo-down-page">
        <div className="dbo-down-box">
          <img src={Downloading} className="dbo-down-box-left" />
          <div className="dbo-down-box-right">
            <div className="dbo-down-box-title">
              {formatMessage({ id: 'Download' })}
            </div>
            <div className="dbo-down-box-desc">
              <div className="dbo-down-box-describe">
                {formatMessage({
                  id: 'Please do not close the tabs during the download process.',
                })}
              </div>

              {fileList.map(({ text, type }) => {
                return (
                  <div className="dbo-down-tipbox" key={text}>
                    <div className="dbo-down-tipbox-lf">
                      <img
                        src={type === 'txt' ? TxtIcon : XlsxIcon}
                        className="dbo-down-tipbox-img"
                      />
                      <div className="dbo-down-tipbox-filename">
                        <BubbleCommon
                          tipsMap={null}
                          content={text}
                          placement="top"
                          showIcon={false}
                        >
                          {/* 文件名 */}
                          {text}
                        </BubbleCommon>
                      </div>
                    </div>
                    {/* 下载状态Icon */}
                    <Icon
                      className="dbo-down-tipbox-statusicon"
                      type={status}
                    />
                  </div>
                );
              })}
            </div>
            <div
              id="maximum-tips"
              className={`maximum-tips ${showError ? 'show' : ''}`}
            >
              <p>{errorText}</p>
              {downloadTips === 'data_max' && (
                <p>{formatMessage({ id: 'Please re-select the filter' })}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default connect((state: any) => {
  const { opinionCommon } = state;
  return {
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
  };
})(DownloadPage);
